// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * What each generator writes, and what it means.
 *
 * This is a RECOGNISER, not an allow-list. ComfyUI does not write a fixed pair
 * of keys: it writes `prompt` plus one chunk for every key of `extra_pnginfo`,
 * so a custom node can and does add its own. Every textual chunk in the file is
 * therefore surfaced; this table only decides what to CALL each one.
 *
 * Several of these keywords are also PNG standard keywords with an ordinary
 * meaning — `Comment`, `Software`, `Description`, `Source`, `Title`. A camera or
 * an editor writes those too. So classification looks at the payload as well as
 * the keyword, and anything it cannot place is reported honestly as an
 * unrecognised text chunk rather than guessed at.
 */

export interface Dialect {
  generator: string;
  label: string;
  kind: 'json' | 'text';
  /** False when the classification is a reasonable guess rather than a fact. */
  certain: boolean;
  fileStem: string;
}

function looksLikeJsonObject(text: string): boolean {
  const trimmed = text.trimStart();
  return trimmed.startsWith('{') || trimmed.startsWith('[');
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/** A ComfyUI API graph: an object whose values all carry `class_type`. */
export function isComfyApiGraph(value: unknown): boolean {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const entries = Object.values(value as Record<string, unknown>);
  if (entries.length === 0) return false;
  return entries.every(
    (v) => !!v && typeof v === 'object' && typeof (v as { class_type?: unknown }).class_type === 'string',
  );
}

/** A ComfyUI UI graph: the litegraph canvas, which has a `nodes` array. */
export function isComfyUiGraph(value: unknown): boolean {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  return Array.isArray((value as { nodes?: unknown }).nodes);
}

function slug(keyword: string): string {
  const cleaned = keyword.replace(/[^A-Za-z0-9._-]+/g, '-').replace(/^-+|-+$/g, '');
  return cleaned.length > 0 ? cleaned.slice(0, 48) : 'chunk';
}

export function classifyEntry(keyword: string, text: string, siblingKeywords: string[]): Dialect {
  const parsed = looksLikeJsonObject(text) ? safeParse(text) : null;
  const json = parsed !== null;

  if (keyword === 'prompt' && isComfyApiGraph(parsed)) {
    return {
      generator: 'ComfyUI',
      label: 'API graph — the nodes as ComfyUI executes them',
      kind: 'json',
      certain: true,
      fileStem: 'prompt',
    };
  }
  if (keyword === 'workflow' && isComfyUiGraph(parsed)) {
    return {
      generator: 'ComfyUI',
      label: 'UI graph — the canvas you actually edit',
      kind: 'json',
      certain: true,
      fileStem: 'workflow',
    };
  }
  if (keyword === 'parameters') {
    return {
      generator: 'AUTOMATIC1111 / Forge',
      label: 'generation settings',
      kind: 'text',
      certain: true,
      fileStem: 'parameters',
    };
  }
  if (keyword === 'invokeai_metadata' || keyword === 'invokeai_graph' || keyword === 'invokeai_workflow') {
    return {
      generator: 'InvokeAI',
      label: keyword === 'invokeai_metadata' ? 'generation settings' : 'graph',
      kind: json ? 'json' : 'text',
      certain: true,
      fileStem: slug(keyword),
    };
  }
  if (keyword === 'sd-metadata' || keyword === 'Dream') {
    return {
      generator: 'InvokeAI (legacy)',
      label: 'generation settings',
      kind: json ? 'json' : 'text',
      certain: true,
      fileStem: slug(keyword),
    };
  }
  if (keyword === 'Comment' && json) {
    const record = parsed as Record<string, unknown>;
    const novelAiish = ['steps', 'sampler', 'uc', 'scale', 'seed'].filter((k) => k in record).length >= 3;
    if (novelAiish) {
      return {
        generator: 'NovelAI',
        label: 'generation settings',
        kind: 'json',
        certain: true,
        fileStem: 'novelai',
      };
    }
  }
  if (keyword === 'Software' && /novelai/i.test(text)) {
    return {
      generator: 'NovelAI',
      label: 'the tool that wrote the file',
      kind: 'text',
      certain: true,
      fileStem: 'novelai-software',
    };
  }
  if (keyword === 'XML:com.adobe.xmp' || /^<\?xpacket/.test(text.trimStart())) {
    return {
      generator: 'XMP',
      label: 'an XMP packet — editors and cameras write these too',
      kind: 'text',
      certain: true,
      fileStem: 'xmp',
    };
  }

  // Anything else. If the file also carries a ComfyUI `prompt`, an unknown key
  // is almost certainly one of ComfyUI's own `extra_pnginfo` entries — but
  // "almost certainly" is what `certain: false` is for.
  if (siblingKeywords.includes('prompt')) {
    return {
      generator: 'ComfyUI',
      label: 'an extra key written alongside the graph',
      kind: json ? 'json' : 'text',
      certain: false,
      fileStem: slug(keyword),
    };
  }

  const STANDARD = ['Title', 'Author', 'Description', 'Copyright', 'Creation Time', 'Software', 'Disclaimer', 'Warning', 'Source', 'Comment'];
  return {
    generator: STANDARD.includes(keyword) ? 'a standard PNG text chunk' : 'unrecognised',
    label: STANDARD.includes(keyword) ? 'ordinary file description, not a generation recipe' : 'text this tool does not recognise',
    kind: json ? 'json' : 'text',
    certain: false,
    fileStem: slug(keyword),
  };
}

/** The dialect of a payload recovered from the pixel plane. */
export function stealthDialect(text: string): Dialect {
  const parsed = looksLikeJsonObject(text) ? safeParse(text) : null;
  if (parsed !== null) {
    return {
      generator: 'NovelAI',
      label: 'generation settings, hidden in the pixels',
      kind: 'json',
      certain: false,
      fileStem: 'hidden-recipe',
    };
  }
  return {
    generator: 'AUTOMATIC1111 / Forge / NovelAI',
    label: 'generation settings, hidden in the pixels',
    kind: 'text',
    certain: false,
    fileStem: 'hidden-recipe',
  };
}
